// HomeActivityItem.jsx

import React, { useMemo } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';

// Utilities
import { ACTIVITY_FEED_ITEM_KIND } from 'node/activity/utils/buildActivityFeedItems';
import { buildActivityFieldDefLookup } from 'node/activity/utils/formatActivityFieldChange';
import { buildActivityFieldLabelLookup } from 'node/activity/utils/formatActivitySystemMessages';

// CSS
import './HomeActivityItem.css';

// Components
import ActivitySystemEvent from 'node/activity/items/ActivitySystemEvent';
import Comment from 'node/form/fields/comments/comment/Comment';

/**
 * HomeActivityItem
 *
 * One activity row inside a burst card.
 *
 * @param {object} props
 * @param {object} props.item - Merged feed item from usePersonalGlobalActivityFeed.
 * @param {function} [props.onOpenNode] - Opens a node via type policy (Peek or Workspace).
 * @param {'datetime'|'time'} [props.timestampStyle] - Full date+time, or time only.
 * @returns {ReactNode}
 */
const HomeActivityItem = React.memo(({
  item,
  onOpenNode,
  timestampStyle = 'datetime',
}) => {
  const { getType } = useNodeTypes();
  const node = item?.node || null;

  const fieldLabels = useMemo(() => {
    const typeKey = typeof node?.type === 'string' ? node.type.trim() : '';
    const typeDef = typeKey ? (getType(typeKey) || null) : null;
    return buildActivityFieldLabelLookup(node, typeDef);
  }, [getType, node]);

  const fieldDefs = useMemo(
    () => buildActivityFieldDefLookup(node),
    [getType, node]
  );

  const typeLabel = useMemo(() => {
    const typeKey = typeof node?.type === 'string' ? node.type.trim() : '';
    if (!typeKey) return '';
    return String(getType(typeKey)?.label || '').trim();
  }, [getType, node?.type]);

  const handleOpenNode = () => {
    if (typeof onOpenNode === 'function' && node) {
      onOpenNode(node);
    }
  };

  if (!item || !node) return null;

  return (
    <div className="home-activity-item-container">
      {item.kind === ACTIVITY_FEED_ITEM_KIND.SYSTEM ? (
        <ActivitySystemEvent
          event={item.event}
          fieldDefs={fieldDefs}
          fieldLabels={fieldLabels}
          node={node}
          reactions={item.reactions || []}
          timestampStyle={timestampStyle}
          typeLabel={typeLabel}
        />
      ) : null}

      {item.kind === ACTIVITY_FEED_ITEM_KIND.COMMENT ? (
        <Comment
          node={node}
          comment={item.comment}
          comments={item.nodeComments || []}
          reactions={item.reactions || []}
          replies={[]}
          repliesCount={0}
          hasMoreReplies={false}
          showReplies={{}}
          displayedReplies={[]}
          timestampStyle={timestampStyle}
          onDeleteClicked={() => {}}
          onReplyClicked={() => handleOpenNode()}
          onToggleMoreReplies={() => {}}
          onToggleReplies={() => {}}
        />
      ) : null}
    </div>
  );
});

HomeActivityItem.displayName = 'HomeActivityItem';

export default HomeActivityItem;
